import type { ReactNode } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type PageSlot = number | 'gap';

/**
 * Which page numbers to show: the first, the last, and the current one with a neighbour each side.
 * A gap that would hide just one page shows that page instead. Pure, exported for tests.
 */
export function pageWindow(current: number, total: number): PageSlot[] {
  if (total <= 0) return [];
  const keep = new Set([1, total, current - 1, current, current + 1].filter((n) => n >= 1 && n <= total));
  const sorted = [...keep].sort((a, b) => a - b);
  const out: PageSlot[] = [];
  sorted.forEach((n, i) => {
    const prev = sorted[i - 1];
    if (prev !== undefined && n - prev === 2) out.push(prev + 1);
    else if (prev !== undefined && n - prev > 2) out.push('gap');
    out.push(n);
  });
  return out;
}

export type PaginationLabels = {
  /** The nav's accessible name ("Pages"). */
  nav: string;
  previous: ReactNode;
  next: ReactNode;
  /** Accessible name of a number ("Page 3"). */
  page: (n: number) => string;
};

export type PaginationProps = {
  /** 1-based. */
  page: number;
  pageCount: number;
  labels: PaginationLabels;
  /** Real links (crawlable, rel prev/next). Use this for anything a search engine should follow. */
  hrefFor?: (page: number) => string;
  /** Client-side paging instead of links. */
  onPageChange?: (page: number) => void;
  /** Number formatting for the page numbers (Finnish uses no-break spaces in thousands). */
  formatPage?: (n: number) => ReactNode;
  className?: string;
};

const cell =
  'inline-flex h-10 min-w-10 items-center justify-center gap-1 rounded-jc-control px-4 text-sm font-semibold tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page';
const idle = 'text-jc-ink hover:bg-jc-elevated';

/** Numbered pages with previous and next. Hidden when there is a single page. */
export function Pagination({
  page,
  pageCount,
  labels,
  hrefFor,
  onPageChange,
  formatPage = (n) => n,
  className,
}: PaginationProps) {
  if (pageCount <= 1) return null;

  const control = (
    target: number,
    content: ReactNode,
    opts: { label?: string; rel?: string; current?: boolean; extra?: string },
  ) => {
    const cls = cx(cell, opts.current ? 'bg-jc-action text-jc-on-action' : idle, opts.extra);
    if (opts.current) {
      return (
        <span aria-current="page" aria-label={opts.label} className={cls}>
          {content}
        </span>
      );
    }
    return hrefFor ? (
      <a href={hrefFor(target)} rel={opts.rel} aria-label={opts.label} className={cls}>
        {content}
      </a>
    ) : (
      <button type="button" aria-label={opts.label} onClick={() => onPageChange?.(target)} className={cls}>
        {content}
      </button>
    );
  };

  const edge = (target: number, enabled: boolean, rel: 'prev' | 'next', content: ReactNode) =>
    enabled ? (
      control(target, content, { rel })
    ) : (
      <span aria-disabled="true" className={cx(cell, 'cursor-not-allowed text-jc-ink-secondary opacity-50')}>
        {content}
      </span>
    );

  return (
    <nav aria-label={labels.nav} className={cx('@container w-full', className)}>
      <ul className="flex flex-wrap items-center justify-center gap-1">
        <li>
          {edge(
            page - 1,
            page > 1,
            'prev',
            <>
              <Icon icon={CaretLeft} size="sm" />
              <span className="@max-md:sr-only">{labels.previous}</span>
            </>,
          )}
        </li>
        {pageWindow(page, pageCount).map((slot, i) => (
          // Narrow containers (phones) keep previous, the current page with its neighbours, and next;
          // the first, last and gaps return from 28rem. Previous and Next stay reachable either way.
          <li
            key={slot === 'gap' ? `gap${i}` : slot}
            className={slot === 'gap' || Math.abs(slot - page) > 1 ? '@max-md:hidden' : undefined}
          >
            {slot === 'gap' ? (
              <span
                aria-hidden="true"
                className="inline-flex h-10 min-w-6 items-center justify-center text-sm text-jc-ink-secondary"
              >
                …
              </span>
            ) : (
              control(slot, formatPage(slot), { label: labels.page(slot), current: slot === page })
            )}
          </li>
        ))}
        <li>
          {edge(
            page + 1,
            page < pageCount,
            'next',
            <>
              <span className="@max-md:sr-only">{labels.next}</span>
              <Icon icon={CaretRight} size="sm" />
            </>,
          )}
        </li>
      </ul>
    </nav>
  );
}
